// ============================================================================
// REPLACE admin-ui/src/pages/VendorConnections.tsx with this version.
//
// Adds Rohana / Torque / RoughCountry rows backed by the daily dry-run-diff.json
// digest. Each row shows: clearance count + request-quote count + in-stock
// count + feed date. Preserves the existing minimal WP/ATD/Vision layout.
// ============================================================================

import { Page, Layout, Card, BlockStack, InlineStack, Text, Badge, Banner, Spinner } from '@shopify/polaris';
import { useQuery } from '@tanstack/react-query';
import {
  getVendorConnections,
  getFeedStatus,
  getVendorDryRunDigest,
  type VendorDryRunDigestEntry,
} from '../api/feeds';

type ConnState = 'connected' | 'error' | 'unknown' | 'stale' | 'missing' | 'not_provided';

function StatusBadge({ state }: { state: ConnState }) {
  if (state === 'connected') return <Badge tone="success">Connected</Badge>;
  if (state === 'error') return <Badge tone="critical">Error</Badge>;
  if (state === 'stale') return <Badge tone="warning">Stale</Badge>;
  if (state === 'missing') return <Badge tone="warning">Missing</Badge>;
  if (state === 'not_provided') return <Badge tone="info">Not provided</Badge>;
  return <Badge tone="attention">Unknown</Badge>;
}

function fmtTime(iso: string | null): string {
  if (!iso) return 'Never';
  const d = new Date(iso);
  return d.toLocaleString();
}

export function VendorConnections() {
  const conn = useQuery({ queryKey: ['vendor-connections'], queryFn: getVendorConnections, refetchInterval: 30_000 });
  const feed = useQuery({ queryKey: ['feed-status'], queryFn: getFeedStatus, refetchInterval: 30_000 });
  const dryRun = useQuery({ queryKey: ['vendor-dry-run-digest'], queryFn: getVendorDryRunDigest, refetchInterval: 60_000 });

  if (conn.isLoading || feed.isLoading) return <Page title="Vendor Connections"><Spinner /></Page>;
  if (conn.error) return <Page title="Vendor Connections"><Banner tone="critical">{(conn.error as Error).message}</Banner></Page>;

  const wp = conn.data?.wheelpros;
  const atd = conn.data?.atd;
  const vision = conn.data?.vision;
  const wpCatalog = wp?.feeds?.catalog;
  const wpInventory = wp?.feeds?.inventory;
  const atdCombined = atd?.feeds?.combined;
  const visionMsrp = vision?.feeds?.msrp;
  const visionInv = vision?.feeds?.inv_update;

  const aggregateState = (states: Array<ConnState | undefined>): ConnState => {
    const present = states.filter((s): s is ConnState => Boolean(s));
    if (present.length === 0) return 'unknown';
    if (present.includes('connected')) return 'connected';
    const order: ConnState[] = ['error', 'missing', 'stale', 'not_provided', 'unknown'];
    for (const s of order) if (present.includes(s)) return s;
    return 'unknown';
  };
  const mostRecent = (times: Array<string | null | undefined>): string | null => {
    const t = times.filter((x): x is string => Boolean(x));
    if (t.length === 0) return null;
    return t.sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];
  };

  const wpAgg = {
    state: aggregateState([wpCatalog?.connection_state as ConnState, wpInventory?.connection_state as ConnState]),
    lastPull: mostRecent([wpCatalog?.last_pull_at, wpInventory?.last_pull_at]),
  };
  const atdAgg = {
    state: aggregateState([atdCombined?.connection_state as ConnState]),
    lastPull: mostRecent([atdCombined?.last_pull_at]),
  };
  const visionAgg = {
    state: aggregateState([visionMsrp?.connection_state as ConnState, visionInv?.connection_state as ConnState]),
    lastPull: mostRecent([visionMsrp?.last_pull_at, visionInv?.last_pull_at]),
  };

  const rohana = dryRun.data?.vendors?.rohana;
  const torque = dryRun.data?.vendors?.torque;
  const rc = dryRun.data?.vendors?.roughcountry;
  // 2026-07-01: 3 TireWeb-SOAP vendors (KG1/Method/WD Atlanta) now emit
  // dry-run-diff.json alongside the DB write, so they hydrate the same
  // DryRunVendorRow shape. AXE stays present as an explicit "Not provided"
  // row while we're blocked on the vendor source spec — better to see
  // one un-plumbed vendor than to hide it entirely.
  const kg1 = dryRun.data?.vendors?.kg1;
  const method = dryRun.data?.vendors?.method;
  const wd = dryRun.data?.vendors?.wd;
  const axe = dryRun.data?.vendors?.axe;

  return (
    <Page title="Vendor Connections" subtitle="Catalog + inventory health, per vendor">
      <Layout>
        <Layout.Section>
          <BlockStack gap="300">
            <VendorRow name="Wheel Pros" state={wpAgg.state} lastPull={wpAgg.lastPull} />
            <VendorRow name="ATD"        state={atdAgg.state} lastPull={atdAgg.lastPull} />
            <VendorRow name="Vision Wheel" state={visionAgg.state} lastPull={visionAgg.lastPull} />
            <DryRunVendorRow name="KG1 Forged"    entry={kg1} />
            <DryRunVendorRow name="Method Race Wheels"       entry={method} />
            <DryRunVendorRow name="WD Atlanta"    entry={wd} />
            <DryRunVendorRow name="Rohana"       entry={rohana} />
            <DryRunVendorRow name="Torque"       entry={torque} />
            <DryRunVendorRow name="RoughCountry" entry={rc} />
            <DryRunVendorRow name="AXE"          entry={axe} />
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

function VendorRow({ name, state, lastPull }: { name: string; state: ConnState; lastPull: string | null }) {
  const okStates: ConnState[] = ['connected'];
  const isOk = okStates.includes(state);
  const alertText = !isOk ? alertMessage(state) : null;
  return (
    <Card>
      <BlockStack gap="200">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h3" variant="headingMd">{name}</Text>
          <StatusBadge state={state} />
        </InlineStack>
        <InlineStack gap="200">
          <Text as="span" variant="bodySm" tone="subdued">Last update:</Text>
          <Text as="span" variant="bodySm">{fmtTime(lastPull)}</Text>
        </InlineStack>
        {alertText ? (
          <Text as="span" variant="bodySm" tone="critical">{alertText}</Text>
        ) : null}
      </BlockStack>
    </Card>
  );
}

// Row backed by dry-run-diff.json — shows in-stock / clearance / request-quote
// counts so the operator sees the latest daily feed read at a glance.
function DryRunVendorRow({ name, entry }: { name: string; entry?: VendorDryRunDigestEntry }) {
  if (!entry || !entry.available) {
    return (
      <Card>
        <BlockStack gap="200">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h3" variant="headingMd">{name}</Text>
            <Badge tone="warning">Missing</Badge>
          </InlineStack>
          <Text as="span" variant="bodySm" tone="critical">No dry-run diff in S3 yet.</Text>
        </BlockStack>
      </Card>
    );
  }
  const s = entry.summary ?? {};
  const date = entry.date ?? '—';
  const mode = entry.mode ?? '—';
  const inStock = (s.in_stock ?? s.in_stock_tn_or_nv ?? 0) as number;
  const clearance = (s.is_clearance ?? 0) as number;
  const requestQuote = (s.is_request_quote ?? 0) as number;
  const total = (s.total_rows ?? 0) as number;
  const salePriceCol = (s as { sale_price_column_present?: boolean }).sale_price_column_present;

  return (
    <Card>
      <BlockStack gap="200">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h3" variant="headingMd">{name}</Text>
          <Badge tone="success">Connected</Badge>
        </InlineStack>
        <InlineStack gap="200">
          <Text as="span" variant="bodySm" tone="subdued">Feed date:</Text>
          <Text as="span" variant="bodySm">{date}</Text>
          <Text as="span" variant="bodySm" tone="subdued">Mode:</Text>
          <Text as="span" variant="bodySm">{mode}</Text>
        </InlineStack>
        <InlineStack gap="200" wrap>
          <Badge tone="info">{`${total.toLocaleString()} rows`}</Badge>
          <Badge tone="success">{`${inStock.toLocaleString()} in stock`}</Badge>
          {clearance > 0 && <Badge tone="warning">{`${clearance.toLocaleString()} clearance`}</Badge>}
          {requestQuote > 0 && <Badge tone="attention">{`${requestQuote.toLocaleString()} request-quote`}</Badge>}
        </InlineStack>
        {name === 'Torque' && salePriceCol === false ? (
          <Text as="span" variant="bodySm" tone="critical">
            Sale Price column (AK) missing from SavedSearch_3901 — clearance cannot be flagged until added.
          </Text>
        ) : null}
      </BlockStack>
    </Card>
  );
}

function alertMessage(state: ConnState): string {
  switch (state) {
    case 'error':       return 'Last pull failed — check vendor credentials or feed availability.';
    case 'stale':       return 'No fresh pull in the last 36 hours.';
    case 'missing':     return 'No data received yet.';
    case 'not_provided':return 'Vendor account does not provide this feed.';
    case 'unknown':     return 'No status available yet.';
    default:            return '';
  }
}

